import React, { useEffect, useRef, useState } from 'react';
import OrderDesign from '../OrderDesign';

/**
 * Simple, independent attack animation component
 * Animates a duplicate minimized order from attacker slot to target slot
 * 
 * @param {Object} props
 * @param {Object} props.order - Order data (minimized)
 * @param {Object} props.fromSlot - {zone, index, playerId} of attacker
 * @param {Object} props.toSlot - {zone, index, playerId} of target
 * @param {boolean} props.attackerSurvived - Whether attacker survived
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {Function} props.onDiveComplete - Callback when dive reaches smallest size (for explosions)
 *   Receives target position info: { x, y, slotElement, rect }
 * @param {number} props.animationSpeedMultiplier - Speed multiplier (1 = normal, 0.5 = 2x slower, 0.1 = 10x slower)
 */
function AttackAnimation({ 
  order, 
  fromSlot,
  toSlot,
  outcome,
  attackerSurvived, 
  onComplete, 
  onDiveComplete,
  animationSpeedMultiplier = 1
}) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isReady, setIsReady] = useState(false); // ✅ FIX: Don't render until we have valid position
  const diveCompleteFiredRef = useRef(false);
  const animationRef = useRef(null);
  const isAnimatingRef = useRef(false); // ✅ FIX: Track if animation is running to prevent restarts
  const hasStartedRef = useRef(false); // ✅ FIX: Track if animation has started at least once
  const targetPositionRef = useRef(null); // ✅ FIX: Store target position for damage animation

  // Animation timing - scaled by speed multiplier
  // At 100% speed (multiplier=1): normal durations
  // At 50% speed (multiplier=0.5): 2x longer durations
  // At 10% speed (multiplier=0.1): 10x longer durations
  const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
  const DIVE_DURATION = Math.round(400 / speedFactor); // ms to reach target
  const RETURN_DURATION = Math.round(300 / speedFactor); // ms to return (if survived)
  const DIVE_SCALE = 0.4; // 60% size reduction = 40% of original

  // Convert gameState zone names to UI zone names (unit slots only)
  const getUIZoneName = (zone, playerId) => {
    if (zone === 'opponent_drone' || zone === 'your_drone') return zone;
    if (playerId === 'your') {
      return zone === 'front' ? 'your_front' : 'your_flank';
    } else {
      return zone === 'front' ? 'opponent_front' : 'opponent_flank';
    }
  };

  // Normalize backend zone 'drone' to UI drone zone (opponent_drone / your_drone) using playerId
  const getDroneUIZone = (zone, playerId) => {
    if (zone === 'opponent_drone' || zone === 'your_drone') return zone;
    if (zone === 'drone') return playerId === 'opponent' ? 'opponent_drone' : 'your_drone';
    return null;
  };

  useEffect(() => {
    if (!fromSlot || !toSlot) {
      console.warn('⚠️ AttackAnimation: Missing fromSlot or toSlot', { fromSlot, toSlot });
      // ✅ FIX: Call onComplete so attack is removed from pendingAttacks
      if (onComplete) {
        console.log('🧹 AttackAnimation: Calling onComplete due to missing slot data');
        onComplete();
      }
      return;
    }

    // ✅ FIX: Prevent restarting animation if it's already running
    // Only start animation once - subsequent renders should not restart it
    if (isAnimatingRef.current || hasStartedRef.current) {
      console.log('⚠️ AttackAnimation: Animation already started, skipping');
      return;
    }
    
    // ✅ FIX: Reset targetPositionRef when starting new animation to prevent stale positions
    // ✅ BUG FIX: Also reset diveCompleteFiredRef to prevent stale state
    targetPositionRef.current = null;
    diveCompleteFiredRef.current = false;

    console.log(`🎬 AttackAnimation: Starting animation`, {
      order: order?.name,
      fromSlot,
      toSlot,
      attackerSurvived
    });

    // Mark that animation has started
    hasStartedRef.current = true;
    isAnimatingRef.current = true;

    // Find slot elements using data attributes
    const findSlotElement = (zoneName, index) => {
      // ✅ DEBUG: Log what we're looking for
      console.log(`🔍 findSlotElement: Looking for zone="${zoneName}", index=${index}`);
      
      // Try to find element with data attributes
      const selector = `[data-slot-zone="${zoneName}"][data-slot-index="${index}"]`;
      let element = document.querySelector(selector);
      
      if (element) {
        console.log(`✅ findSlotElement: Found element via selector for zone="${zoneName}", index=${index}`);
        return element;
      }
      
      // Fallback: find by zone and check position
      const zoneElements = document.querySelectorAll(`[data-slot-zone="${zoneName}"]`);
      console.log(`🔍 findSlotElement: Found ${zoneElements.length} elements in zone "${zoneName}"`);
      for (const el of zoneElements) {
        const elIndex = parseInt(el.getAttribute('data-slot-index') || '-1');
        console.log(`🔍 findSlotElement: Checking element with index=${elIndex} (looking for ${index})`);
        if (elIndex === index) {
          console.log(`✅ findSlotElement: Found element via attribute check for zone="${zoneName}", index=${index}`);
          return el;
        }
      }
      
      // ✅ FIX: Removed buggy position calculation fallback
      // The position calculation was incorrect and could match the wrong slot (especially index 0)
      // If the first two methods fail, return null and let the main code use the fallback position calculation
      // which correctly uses toSlot.index and doesn't risk matching the wrong slot
      console.warn(`⚠️ findSlotElement: Could not find element for zone="${zoneName}", index=${index}, returning null`);
      return null;
    };

    // Find drone slot elements (drones use data-drone-zone, data-drone-index, and optionally data-instance-id)
    // Prefer instanceId when available so we hit the correct card even when index differs (e.g. filtered list vs full array)
    const findDroneElement = (droneZoneName, index, targetInstanceId) => {
      if (targetInstanceId) {
        const byInstanceSelector = `[data-drone-zone="${droneZoneName}"][data-instance-id="${targetInstanceId}"]`;
        const elementByInstance = document.querySelector(byInstanceSelector);
        if (elementByInstance) {
          console.log(`✅ findDroneElement: Found drone slot by instanceId for zone="${droneZoneName}"`);
          return elementByInstance;
        }
      }
      const byIndexSelector = `[data-drone-zone="${droneZoneName}"][data-drone-index="${index}"]`;
      const elementByIndex = document.querySelector(byIndexSelector);
      if (elementByIndex) {
        console.log(`✅ findDroneElement: Found drone slot by index for zone="${droneZoneName}", index=${index}`);
        return elementByIndex;
      }
      console.warn(`⚠️ findDroneElement: Could not find drone element for zone="${droneZoneName}", index=${index}${targetInstanceId ? `, instanceId=${targetInstanceId}` : ''}`);
      return null;
    };

    // ✅ NEW: Find general element (for general attacks)
    const findGeneralElement = (playerId) => {
      // General has data-zone="opponent_general" or data-zone="player_general"
      const generalZone = playerId === 'opponent' ? 'opponent_general' : 'player_general';
      const element = document.querySelector(`[data-zone="${generalZone}"]`);
      return element;
    };

    const attackerUIZone = getUIZoneName(fromSlot.zone, fromSlot.playerId);
    
    // ✅ NEW: Check if this is a general attack
    const isGeneralAttack = toSlot.zone === 'general';
    // ✅ DRONE FIX: Check if target is a drone (unit 239 / Kek can target drones)
    const droneUIZone = getDroneUIZone(toSlot.zone, toSlot.playerId);
    let isDroneTarget = droneUIZone != null;
    let targetUIZone;
    let targetSlot;
    
    // ✅ FALLBACK: If outcome has targetInstanceId, try to find a drone with that id (handles wrong targetZone from backend/campaign)
    if (!isGeneralAttack && outcome?.targetInstanceId) {
      const opponentDroneEl = document.querySelector(`[data-drone-zone="opponent_drone"][data-instance-id="${outcome.targetInstanceId}"]`);
      const yourDroneEl = document.querySelector(`[data-drone-zone="your_drone"][data-instance-id="${outcome.targetInstanceId}"]`);
      const droneEl = opponentDroneEl || yourDroneEl;
      if (droneEl) {
        isDroneTarget = true;
        targetUIZone = opponentDroneEl ? 'opponent_drone' : 'your_drone';
        targetSlot = droneEl;
        console.log(`✅ AttackAnimation: Resolved drone target by instanceId (targetZone fallback)`);
      }
    }
    
    if (isGeneralAttack) {
      // For general attacks, find the general element
      targetSlot = findGeneralElement(toSlot.playerId);
      targetUIZone = 'general';
    } else if (isDroneTarget && !targetSlot) {
      // For drone attacks, use data-drone-zone and data-instance-id (or index) so we hit the correct card
      targetUIZone = droneUIZone;
      targetSlot = findDroneElement(droneUIZone, toSlot.index, outcome?.targetInstanceId);
    } else if (!targetSlot) {
      // For unit attacks, use existing logic
      targetUIZone = getUIZoneName(toSlot.zone, toSlot.playerId);
      targetSlot = findSlotElement(targetUIZone, toSlot.index);
    }

    // Find attacker slot
    const attackerSlot = findSlotElement(attackerUIZone, fromSlot.index);

    if (!attackerSlot || !targetSlot) {
      console.error('❌ AttackAnimation: Could not find slot elements:', {
        attackerSlot: !!attackerSlot,
        targetSlot: !!targetSlot,
        attackerUIZone,
        attackerIndex: fromSlot.index,
        targetUIZone,
        targetIndex: toSlot.index,
        isGeneralAttack,
        fromSlot,
        toSlot
      });
      console.log('🔍 AttackAnimation: Available slot elements:', {
        attackerZone: document.querySelectorAll(`[data-slot-zone="${attackerUIZone}"]`).length,
        targetZone: document.querySelectorAll(`[data-slot-zone="${targetUIZone}"]`).length,
        general: document.querySelectorAll(`[data-zone*="general"]`).length
      });
      
      // ✅ FIX: Remove fallback position calculation to prevent wrong damage animations
      // If we can't find the slot elements, don't animate and don't trigger damage animation
      // This prevents incorrect damage animations from appearing in the wrong slots
      console.warn('⚠️ AttackAnimation: Skipping animation because slot elements not found - this prevents wrong damage animations');
      // ✅ FIX: Call onComplete so attack is removed from pendingAttacks (prevents ghost images)
      if (onComplete) {
        console.log('🧹 AttackAnimation: Calling onComplete due to missing slot elements');
        onComplete();
      }
      return;
    }

    // Get actual positions from DOM
    const attackerRect = attackerSlot.getBoundingClientRect();
    const targetRect = targetSlot.getBoundingClientRect();
    
    const fromX = attackerRect.left + attackerRect.width / 2;
    const fromY = attackerRect.top + attackerRect.height / 2;
    const toX = targetRect.left + targetRect.width / 2;
    const toY = targetRect.top + targetRect.height / 2;

    // ✅ FIX: Store target position and slot info for damage animation
    // ✅ BUG FIX: Ensure we're storing the TARGET position, not the attacker position
    // Validate that toX/toY are different from fromX/fromY to catch bugs
    // ✅ BUG FIX: Also validate that we're using the target slot, not the attacker slot
    if (Math.abs(toX - fromX) < 10 && Math.abs(toY - fromY) < 10) {
      console.error('❌ AttackAnimation: Target position is too close to attacker position - likely a bug!', {
        fromX, fromY, toX, toY,
        attackerZone: attackerUIZone,
        targetZone: targetUIZone,
        attackerIndex: fromSlot.index,
        targetIndex: toSlot.index
      });
      // Don't store invalid position - this would cause damage animation at wrong location
      // ✅ FIX: Call onComplete so attack is removed from pendingAttacks (prevents ghost images)
      if (onComplete) {
        console.log('🧹 AttackAnimation: Calling onComplete due to invalid position');
        onComplete();
      }
      return;
    }
    
    // ✅ BUG FIX: Double-check that we're using the target slot, not attacker slot
    if (targetSlot === attackerSlot) {
      console.error('❌ AttackAnimation: Target slot is the same as attacker slot - this is a bug!', {
        attackerZone: attackerUIZone,
        targetZone: targetUIZone,
        attackerIndex: fromSlot.index,
        targetIndex: toSlot.index
      });
      // Don't store invalid position
      // ✅ FIX: Call onComplete so attack is removed from pendingAttacks (prevents ghost images)
      if (onComplete) {
        console.log('🧹 AttackAnimation: Calling onComplete due to slot mismatch');
        onComplete();
      }
      return;
    }
    
    targetPositionRef.current = {
      x: toX,
      y: toY,
      slotElement: targetSlot,
      rect: targetRect,
      // ✅ BUG FIX: Store zone/index info for validation
      targetZone: targetUIZone,
      targetIndex: toSlot.index,
      attackerZone: attackerUIZone,
      attackerIndex: fromSlot.index
    };
    
    // ✅ DEBUG: Log target position for damage animation
    console.log(`🎯 AttackAnimation: Target position stored for damage animation:`, {
      x: toX,
      y: toY,
      targetZone: targetUIZone,
      targetIndex: toSlot.index,
      slotElement: !!targetSlot,
      rect: targetRect,
      attackerPosition: { x: fromX, y: fromY, zone: attackerUIZone, index: fromSlot.index } // Log attacker position for comparison
    });

    // ✅ FIX: Set isReady to true now that we have valid positions
    setIsReady(true);
    animateAttack(fromX, fromY, toX, toY);
  }, [fromSlot, toSlot, outcome, attackerSurvived, onComplete, onDiveComplete]);

  // Ref to store safety timeout
  const safetyTimeoutRef = useRef(null);
  
  const animateAttack = (fromX, fromY, toX, toY) => {
    // ✅ FIX: Cancel any existing animation before starting new one
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // ✅ FIX: Clear any existing safety timeout
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
    }

    // Set initial position
    setPosition({ x: fromX, y: fromY });
    setScale(1);
    diveCompleteFiredRef.current = false;
    
    // ✅ FIX: Safety timeout - if animation doesn't complete within expected time, force complete
    // This prevents ghost animations from staying on screen forever if something goes wrong
    const maxAnimationTime = (DIVE_DURATION + RETURN_DURATION + 500) * 2; // 2x expected time + buffer
    safetyTimeoutRef.current = setTimeout(() => {
      console.warn('⚠️ AttackAnimation: Safety timeout triggered - animation took too long', {
        order: order?.name,
        fromSlot,
        toSlot,
        maxAnimationTime
      });
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      isAnimatingRef.current = false;
      if (onComplete) {
        console.log('🧹 AttackAnimation: Calling onComplete from safety timeout');
        onComplete();
      }
    }, maxAnimationTime);

    // Animate dive to target
    const startTime = Date.now();
    const animateDive = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / DIVE_DURATION, 1);
      
      // Easing function (ease-in-out)
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      // Interpolate position
      const currentX = fromX + (toX - fromX) * eased;
      const currentY = fromY + (toY - fromY) * eased;
      
      // Interpolate scale (dive to 40% at target)
      const currentScale = 1 - (1 - DIVE_SCALE) * eased;
      
      setPosition({ x: currentX, y: currentY });
      setScale(currentScale);
      
      // ✅ FIX: Fire dive complete callback at EXACT smallest size (when scale reaches DIVE_SCALE)
      // This MUST fire regardless of whether attacker survives - it fires at the dive's lowest point
      // Trigger when we're at or very close to the minimum scale (lowest point of dive)
      // Check if we've reached the target scale (within 0.02 tolerance for frame timing)
      if (!diveCompleteFiredRef.current) {
        // Check if we're at the minimum scale or have passed it
        const isAtMinimum = currentScale <= DIVE_SCALE + 0.02;
        // Also check if progress is complete (reached target)
        const hasReachedTarget = progress >= 0.99;
        
        if (isAtMinimum || hasReachedTarget) {
          diveCompleteFiredRef.current = true;
          // ✅ FIX: ALWAYS fire callback if we have target position, regardless of attacker survival
          // The damage animation should play at the target position when dive completes
          if (onDiveComplete && targetPositionRef.current) {
            // ✅ CRITICAL FIX: Validate target position before calling onDiveComplete
            const pos = targetPositionRef.current;
            if (pos && typeof pos.x === 'number' && typeof pos.y === 'number' && 
                !isNaN(pos.x) && !isNaN(pos.y) && pos.x > 0 && pos.y > 0) {
              // Pass target position info to callback for damage animation
              onDiveComplete(targetPositionRef.current);
            } else {
              console.error('❌ AttackAnimation: Invalid target position, skipping damage animation', {
                targetPosition: pos,
                x: pos?.x,
                y: pos?.y,
                attackerSurvived
              });
            }
          } else {
            console.error('❌ AttackAnimation: Cannot trigger damage animation', {
              onDiveComplete: !!onDiveComplete,
              targetPositionRef: !!targetPositionRef.current,
              targetPositionValue: targetPositionRef.current,
              attackerSurvived
            });
          }
        }
      }
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateDive);
      } else {
        // Reached target
        if (attackerSurvived) {
          // ✅ FIX: Reduced delay from 100ms to 50ms for smoother flow
          setTimeout(() => {
            const returnStartTime = Date.now();
            const animateReturn = () => {
              const returnElapsed = Date.now() - returnStartTime;
              const returnProgress = Math.min(returnElapsed / RETURN_DURATION, 1);
              
              // Easing for return
              const returnEased = returnProgress < 0.5
                ? 2 * returnProgress * returnProgress
                : 1 - Math.pow(-2 * returnProgress + 2, 2) / 2;
              
              // Interpolate back to start
              const returnX = toX + (fromX - toX) * returnEased;
              const returnY = toY + (fromY - toY) * returnEased;
              
              // Scale back to 100%
              const returnScale = DIVE_SCALE + (1 - DIVE_SCALE) * returnEased;
              
              setPosition({ x: returnX, y: returnY });
              setScale(returnScale);
              
              if (returnProgress < 1) {
                animationRef.current = requestAnimationFrame(animateReturn);
              } else {
                // Animation complete
                isAnimatingRef.current = false; // ✅ FIX: Mark animation as complete
                // ✅ FIX: Clear safety timeout since animation completed normally
                if (safetyTimeoutRef.current) {
                  clearTimeout(safetyTimeoutRef.current);
                  safetyTimeoutRef.current = null;
                }
                if (onComplete) {
                  onComplete();
                }
              }
            };
            animationRef.current = requestAnimationFrame(animateReturn);
          }, 50); // ✅ FIX: Reduced from 100ms to 50ms
        } else {
          // Attacker died - no return animation
          isAnimatingRef.current = false; // ✅ FIX: Mark animation as complete
          // ✅ FIX: Clear safety timeout since animation completed normally
          if (safetyTimeoutRef.current) {
            clearTimeout(safetyTimeoutRef.current);
            safetyTimeoutRef.current = null;
          }
          if (onComplete) {
            onComplete();
          }
        }
      }
    };
    
    animationRef.current = requestAnimationFrame(animateDive);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // ✅ FIX: Clear safety timeout on unmount
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
      // ✅ FIX: Reset animation state on unmount
      isAnimatingRef.current = false;
      hasStartedRef.current = false;
    };
  }, []);

  // ✅ FIX: Don't render until we have a valid position
  // This prevents the ghost image appearing at (0,0) (upper left corner) before animation starts
  // or when animation fails to initialize due to missing slot elements
  if (!isReady) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: `translate(-50%, -50%) scale(${scale})`,
        zIndex: 10000,
        pointerEvents: 'none',
        transition: 'none'
      }}
    >
      <OrderDesign
        order={order}
        minimal={true}
        gameState={null}
      />
    </div>
  );
}

export default AttackAnimation;
